import { createSelector, Selector } from '@reduxjs/toolkit'
// @ts-ignore
import { slice as profileSlice } from '@polleverywhere/redux/slices/profile'
import { selectActivePresenter, selectIsExistingParticipant, selectNeedsToPay, selectSelfRegistrationAllowed } from '../activePresenter'
import { selectCurrentActivity, selectPinnedActivity, selectSkippedRegister, Activity, selectForcedRegister } from '../responsePage'

export enum REGISTRATION_STATUS {
  OK = 'ok',
  UNPAID = 'unpaid',
  REQUIRED = 'required',
  SUGGESTED = 'suggested',
  CONTACT = 'contact'
}

export function activitySuggestsRegistration (activity: Activity | undefined) {
  return activity &&
    !activity.anonymous &&
    (activity.type !== 'competition' && !activity.inCompetition) &&
    activity.gradingEnabled
}

export function activityRequiresRegistration (activity: Activity | undefined) {
  return activity &&
    (activity.registeredParticipantsOnly
      || !!activity.polls?.some(p => p.registeredParticipantsOnly)
    )
}

export const selectLoggedInAsPresenter: Selector<unknown, boolean> = (state: any) => state[profileSlice.name].user?.login === selectActivePresenter(state).login
export const selectRegisteredParticipantsOnly = createSelector(
  selectCurrentActivity,
  selectPinnedActivity,
  selectForcedRegister,
  (current, pinned, forced) => activityRequiresRegistration(current) || activityRequiresRegistration(pinned) || forced
)

export const selectRegistrationSuggested = createSelector(
  (state) => selectActivePresenter(state).registrationInfo.registrationSuggested,
  selectIsExistingParticipant,
  (state) => activitySuggestsRegistration(selectCurrentActivity(state)),
  (registrationSuggested, isRegistered, currentSuggests) => {
    return registrationSuggested && !isRegistered && currentSuggests
  }
)

export const selectRegistrationStatus = createSelector(
  selectIsExistingParticipant,
  selectNeedsToPay,
  selectSelfRegistrationAllowed,
  selectRegisteredParticipantsOnly,
  selectRegistrationSuggested,
  selectLoggedInAsPresenter,
  selectSkippedRegister,
  (isRegistered, needsToPay, selfRegistrationAllowed, registeredParticipantsOnly, registrationSuggested, loggedInAsPresenter, skippedRegister) => {
    const showPaywall = !registeredParticipantsOnly && isRegistered && needsToPay && !loggedInAsPresenter
    const showRegisteredParticipantsOnly = registeredParticipantsOnly && !isRegistered && selfRegistrationAllowed
    const showContactPresenterToRegister = registeredParticipantsOnly && !isRegistered && !selfRegistrationAllowed

    const unpaidAndUnregistered = !isRegistered && needsToPay
    const showRegisterForCredit = !registeredParticipantsOnly && (unpaidAndUnregistered || registrationSuggested)

    if (showPaywall) {
      return REGISTRATION_STATUS.UNPAID
    } else if (showRegisteredParticipantsOnly) {
      return REGISTRATION_STATUS.REQUIRED
    } else if (showContactPresenterToRegister) {
      return REGISTRATION_STATUS.CONTACT
    } else if (showRegisterForCredit && !skippedRegister) {
      return REGISTRATION_STATUS.SUGGESTED
    }

    return REGISTRATION_STATUS.OK
  }
)
